'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import { KiaoModal } from '@kiao-voice/ui-kit'
import { OtpForm } from './otp-form'
import { AuthFlash } from './auth-flash'
import type { requestOtp, verifyOtp, OtpVerifyResponse } from './auth-storage'
import { readReferralCode, clearReferralCode } from '@/features/referral/referral-storage'

/** Done-step dwell before closing + redirecting (design/js/kiao-login.js). */
const DONE_REDIRECT_DELAY_MS = 1200

export interface AuthModalCampaign {
  /** Public plan name, e.g. "Pro" — never an internal plan code. */
  planName: string
  /**
   * Already-composed length, e.g. "14 天" or "1 個月". A prebuilt string
   * because the campaign may state its length in days OR months, and the
   * dialog must restate exactly what the banner offered.
   */
  durationLabel: string
  /** The address the banner collected; the email step is skipped for it. */
  email: string
}

interface AuthModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a successful OTP verify (token already persisted by
   *  `verifyOtp`) once the done step's 1s dwell elapses. The host
   *  refreshes server state and navigates — the default host
   *  (AuthModalProvider) lands on /transcriptions; callers that
   *  pass their own handler control the destination themselves. */
  onAuthenticated?: (result: OtpVerifyResponse) => void
  /** Pre-fill the email step (e.g. the address a guest typed at checkout). */
  prefillEmail?: string
  /** Explanatory banner shown above the form (e.g. why sign-in was required). */
  notice?: string
  /**
   * Campaign variant of the same dialog: restates what is being claimed above
   * the code field, paints the primary button the campaign red, and skips the
   * email step because the banner already collected the address.
   */
  campaign?: AuthModalCampaign
  /** Test seam — passed through to `OtpForm`. */
  requestFn?: typeof requestOtp
  /** Test seam — passed through to `OtpForm`. */
  verifyFn?: typeof verifyOtp
}

/**
 * Unified login/signup dialog — the `.kiao-modal.kiao-login` shell from
 * design/js/kiao-login.js. Auth is OTP-only (email → 6-digit code) and
 * the backend auto-creates the account on first verify, so one flow
 * handles both sign-in and sign-up (`purpose='login'`; request always
 * returns 202, verify auto-creates if needed).
 *
 * Steps: Email → Code (OtpForm) → Done (shared <AuthFlash>: 已登入 /
 * 正在前往批次轉錄…). On done the modal gains `is-done` so the head
 * collapses (the flash centres on its own, never stacking under the
 * email hint). AuthFlash owns the dwell timer; when it elapses the modal
 * closes and `onAuthenticated` fires. Backdrop / Esc / × close at any step.
 */
export function AuthModal({
  open,
  onOpenChange,
  onAuthenticated,
  prefillEmail,
  notice,
  campaign,
  requestFn,
  verifyFn,
}: AuthModalProps) {
  const { t } = useTranslation('web')
  const [done, setDone] = React.useState(false)
  const [verified, setVerified] = React.useState<OtpVerifyResponse | null>(null)
  // Guards the post-auth completion so it runs exactly once whether the flash
  // dwell elapses OR the user dismisses the flash early (Esc / backdrop).
  const completedRef = React.useRef(false)

  // Reopen starts a fresh flow (OtpForm remounts because KiaoModal
  // unmounts its children while closed; only the done state resets here).
  React.useEffect(() => {
    if (open) {
      setDone(false)
      setVerified(null)
      completedRef.current = false
    }
  }, [open])

  const handleVerified = (result: OtpVerifyResponse) => {
    // A referral captured at signup is now delivered (the modal is the primary
    // auth entry, and verify auto-provisions a new account) — drop the stored
    // code so a later signup on this device can't re-send a stale one.
    clearReferralCode()
    setVerified(result)
    setDone(true)
  }

  // Attach the captured referral code (issue #71) to the verify so a referred
  // user who creates their account via the modal (not the /signup page) is
  // captured too. Server captures it only on a brand-new account + while
  // referral.enabled; harmless on an existing-user sign-in.
  const referralCode = readReferralCode() ?? undefined

  const showDone = done && verified !== null

  // Sign-in already succeeded (token stored) once we're in the done state, so
  // closing the flash for ANY reason must still run the host's refresh/redirect
  // — otherwise dismissing the flash strands the user signed-in-but-not-moved.
  const completeAuth = () => {
    if (completedRef.current) return
    completedRef.current = true
    onOpenChange(false)
    if (verified) onAuthenticated?.(verified)
  }

  return (
    <KiaoModal
      open={open}
      onClose={showDone ? completeAuth : () => onOpenChange(false)}
      title={t('auth.modalHeading')}
      subtitle={t('auth.modalSubhead')}
      closeLabel={t('auth.close')}
      // In the done state the head is collapsed, so name the dialog by the
      // visible flash title instead of the (now-hidden) sign-in heading.
      ariaLabel={showDone ? t('auth.doneSignedIn') : undefined}
      className={['kiao-login', showDone ? 'is-done' : null, campaign ? 'kiao-campaign-otp' : null]
        .filter(Boolean)
        .join(' ')}
    >
      {showDone ? (
        <AuthFlash
          icon="check"
          title={t('auth.doneSignedIn')}
          subtitle={t('auth.doneRedirecting')}
          delayMs={DONE_REDIRECT_DELAY_MS}
          onComplete={completeAuth}
        />
      ) : (
        <>
          {campaign ? (
            <p className="otp-recap" data-testid="auth-modal-campaign-recap">
              <span>{t('promoCampaign.otp.recapLabel')}</span>
              <b>
                {t('promoCampaign.otp.recapValue', {
                  plan: campaign.planName,
                  duration: campaign.durationLabel,
                })}
              </b>
            </p>
          ) : null}
          {notice ? (
            <p className="note" role="status" data-testid="auth-modal-notice">
              {notice}
            </p>
          ) : null}
          <OtpForm
            analyticsSource="auth_modal"
            purpose="login"
            codeCtaLabel={campaign ? t('promoCampaign.otp.verifyCta') : t('auth.ctaLoginVerify')}
            initialEmail={campaign ? campaign.email : prefillEmail}
            autoSend={Boolean(campaign)}
            onVerified={handleVerified}
            extraVerifyPayload={{ referralCode }}
            requestFn={requestFn}
            verifyFn={verifyFn}
          />
        </>
      )}
    </KiaoModal>
  )
}
